<template>
  <div id="menu" :class="{ hide: !showMenu }">
    <div class="menu-content">
      <div class="search-bar-container">
        <SearchBar placeholder="Enter Eircode or Address" @select="prepareLocation"></SearchBar>
      </div>
      <div class="menu-block">
        <span class="label" :tooltip="tooltip(tooltipKeys.NETWORK_TOOLTIP)">Service Provider</span>
        <label for="network" style="display: none">Select service provider</label>
        <div class="select-container">
          <select id="network" v-model="network">
            <option value="null" v-if="!network">Choose a Service Provider</option>
            <option
              v-for="(n, index) in networks"
              :key="index"
              :value="n"
            >{{ n.label }}
            </option>
          </select>
        </div>
        <hr v-show="network"/>
        <div v-show="network">
          <span class="label" :tooltip="tooltip(tooltipKeys.TECHNOLOGY_TOOLTIP)">Technology</span>
          <TechnologyButtons @select="selectTechnology"></TechnologyButtons>
        </div>
      </div>
      <div class="menu-block" v-show="network && technology">
        <span class="label" :tooltip="tooltip(tooltipKeys.LEGEND_TOOLTIP)">Legend</span>
        <Legend></Legend>
      </div>
      <div class="menu-block button-wide" :class="{'active': locationCoverageModalState}">
        <span class="button-wide__text" target="_blank" @click="getComparisonAllCoverage"
              :class="{'disabled': !technologyName || !location || !location.address}">
          All coverage at this location
        </span>
      </div>
      <div class="menu-block button-wide">
        <a href="https://www.comreg.ie/compare/#/services" class="button-wide__link" target="_blank">Comparison tool</a>
      </div>
      <div class="menu-block">
        <div class="help-link">
          <span class="help-link__icon help-link__icon--faq" aria-hidden="true"></span>
          <a class="help-link__link" href="/faq" target="_blank">Frequently Asked Questions</a>
        </div>
        <div class="help-link">
          <span class="help-link__icon help-link__icon--queries" aria-hidden="true"></span>
          <a class="help-link__link" href="https://comreg.ie/contact" target="_blank" @click="visitQueries()">Queries about this map?</a>
        </div>
      </div>
      <div class="menu-block">
        <div class="help-link">
          <span class="help-link__icon help-link__icon--disclaimer" aria-hidden="true"></span>
          <a href="/disclaimer" class="help-link__link" target="_blank">ComReg Coverage Map Disclaimer</a>
        </div>
      </div>
    </div>

    <button class="hide-button" @click="toggleMenu()" aria-label="show/hide menu">
      <fa-icon icon="angle-left" class="hide-button__icon" v-if="showMenu" aria-hidden="true"></fa-icon>
      <fa-icon icon="angle-right" class="hide-button__icon" v-else aria-hidden="true"></fa-icon>
    </button>
    <LocationCoverageModal :showModal="showModal" @close-modal="showModal=false"/>
  </div>
</template>

<script>
import SearchBar from '@/components/custom/SearchBar';
import Legend from '@/components/custom/Legend';
import TechnologyButtons from '@/components/custom/TechnologyButtons';
import LocationCoverageModal from '@/components/custom/LocationCoverageModal';
import vSelect from 'vue-select';
import { mapActions, mapGetters } from 'vuex';
import LocationService from '@/services/LocationService';
import * as tooltipKeys from '@/enum/tooltips.enum';
import utils from '@/utils';
import { EventBus } from '@/services/EventBus';
import { QUERIES } from '@/enum/screens.enum';
import { LEGEND } from '@/enum/map-stat-types.enum';

export default {
  data() {
    return {
      showMenu: true,
      tooltipKeys,
      showModal: false
    }
  },
  components: { SearchBar, TechnologyButtons, Legend, vSelect, LocationCoverageModal },
  computed: {
    ...mapGetters('map', ['networks', 'location', 'networkName', 'tooltip', 'technologies', 'technology', 'technologyName',
      'locationPlaceId', 'locationCoverageModalState']),
    network: {
      get() { return this.$store.getters['map/network']; },
      set(v) {
        this.selectNetwork(v);
      }
    }
  },
  methods: {
    ...mapActions('map', ['saveLocation', 'saveNetwork', 'saveTechnology',
      'screenVisited', 'sendMapStat', 'getComparisonAllCoverage']),

    prepareLocation(loc) {
      this.saveLocation(LocationService.transformGeocoded(loc));
      EventBus.$emit('google_place_selected');
    },

    toggleMenu() {
      this.showMenu = !this.showMenu;
    },

    visitQueries() {
      this.screenVisited({ screen: QUERIES });
    },

    saveOptionsInUrlParams() {
      let locationStr = LocationService.stringifyLocationLatLng(this.location);
      let network = utils.url.getParameterByName('network');
      let technology = utils.url.getParameterByName('technology');
      let placeId = utils.url.getParameterByName('place_id');
      let location = utils.url.getParameterByName('location');
      this.$router.push({
        path: 'map',
        query: {
          location: locationStr || location,
          technology: this.technologyName || technology,
          network: this.networkName || network,
          place_id: this.locationPlaceId || placeId
        }
      }).catch(() => {});
    },

    selectNetwork(n) {
      this.saveNetwork(n);
      this.legendChangedSendStat();
    },

    selectTechnology(t) {
      this.saveTechnology(t);
      this.legendChangedSendStat();
    },

    legendChangedSendStat() {
      this.sendMapStat({
        technology: this.technologyName,
        operator: this.networkName,
        lat: this.location && this.location.lat,
        lon: this.location && this.location.lng,
        address: this.location && this.location.address,
        placeId: this.location && this.location.placeId,
        type: LEGEND
      });
    }
  },
  watch: {
    networks: function (networks) {
      let network = utils.url.getParameterByName('network');
      let found = networks.find(v => v.name === network);
      if (found) this.saveNetwork(found);
    },
    technologies: function (technologies) {
      let tech = utils.url.getParameterByName('technology');
      let found = technologies.find(v => v.name === tech);
      if (found) this.saveTechnology(found);
    },
    location: function () {
      this.saveOptionsInUrlParams();
    },
    networkName: function () {
      this.saveOptionsInUrlParams();
    },
    technologyName: function () {
      this.saveOptionsInUrlParams();
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="stylus" scoped>
@import 'nib'

$hideBtnSize = 45px

hr
  margin 20px 0px
  border 0.5px solid #eee

#menu
  column()
  flex-grow 1
  padding 0
  margin 0
  transition 0.5s left ease-in-out
  relative top left
  width $menuWidth
  background #EBEFF2
  z-index 20
  box-shadow 3px -3px 3px rgba(0,0,0, 0.16)
  height 100%
  max-width 90%
  absolute top left

  & .menu-content
    display block
    relative top left
    overflow-x hidden
    overflow-y auto
    height 100%

  &.hide
    left -1 * $menuWidth
    @media screen and (max-width $menuWidth)
      left -90%

  & .hide-button
    background white
    box-shadow 3px 3px 3px rgba(0,0,0, 0.16), 3px -3px 3px rgba(0,0,0, 0.16)
    border none
    border-left 1px solid rgba(0,0,0, 0.16)
    border-radius 0
    absolute top 14px right 0
    right "calc(-0.45 * %s)" % $hideBtnSize
    z-index 21
    width $hideBtnSize * 0.45
    height $hideBtnSize
    font-size 20px
    color #666
    padding 1px
    margin-left 1px

.search-bar-container
  width 100%
  background #EBEFF2
  padding 20px 15px
  box-sizing border-box

.menu-block
  padding 24px
  background white
  margin-bottom 7px
  box-shadow 0 3px 3px rgba(0, 0, 0, 0.16)
  @media screen and (max-width: 768px)
    padding 10px 24px

.button-wide
  padding 12px 16px
  text-align center

  &__link, &__text
    text-transform uppercase
    font-weight 600
    color #018478
    text-decoration unset
    cursor pointer

    &.disabled
      color #666666
      cursor not-allowed

  &.active
    background-color #018478

    .button-wide__text
      color #FFFFFF

.help-link
  row()
  align-items center
  margin-bottom 6px

  &__link
    color #0055a4
    font-size 16px

  &__icon
    square(25px)
    background-size 25px 25px
    margin-right 22px

    &--queries
      background url('/static/img/icons/question-circle.svg') no-repeat

    &--faq
      background url('/static/img/icons/FAQ.svg') no-repeat

    &--disclaimer
      background url('/static/img/icons/ic_Disclaimer.svg') no-repeat

</style>
